/**
 * Lista de tela SEM as métricas de envio — Cliques e a lista de fontes de Leads.
 *
 * Elas têm colunas próprias (cliques, contatos únicos, novos leads) e por isso não passam por
 * `RowsView`, que monta o card a partir das métricas. O desenho em cards, esse elas merecem igual:
 * uma tabela de URLs longas é justamente a que fica mais bagunçada.
 *
 * O card se monta das MESMAS colunas da tabela: a primeira é o cabeçalho do card (ela já traz o
 * nome e o subtítulo da linha), as outras viram rótulo e número. Nada é recalculado no caminho.
 */
import { useEffect } from 'react';
import { v } from '@/components/v2/theme';
import { CARD_SIZES } from '../lib/prefs';
import type { ScreenKey } from '../lib/screens';
import { useEmail } from '../state/store';
import { MetricTable, sortRows, type Col, type MetricTableProps } from './MetricTable';

export function DataView<R>({ screenKey, ...table }: MetricTableProps<R> & { screenKey: ScreenKey }) {
    const { prefs, registerPreview } = useEmail();
    const p = prefs.screens[screenKey];
    const { rows, cols, rowKey } = table;

    // sem array de dependências de propósito: só escreve uma ref, não dispara renderização.
    // Aqui nenhuma coluna vem de preferência, então o registro já é o desenho final.
    useEffect(() => {
        registerPreview({
            kind: 'data',
            rows: rows as readonly unknown[],
            cols: cols as Col<unknown>[],
            rowKey: rowKey as (row: unknown, i: number) => string,
        });
        return () => registerPreview(null);
    });

    if (p.display !== 'cards') return <MetricTable {...table} />;
    return <DataCards rows={sortRows(rows, cols, table.sort ?? null)} cols={cols} rowKey={rowKey} size={p.cardSize} />;
}

export function DataCards<R>({ rows, cols, rowKey, size, flush }: {
    rows: readonly R[];
    cols: Col<R>[];
    rowKey: (row: R, i: number) => string;
    size: string;
    /** Sem respiro em volta: o espaço é de quem chama. */
    flush?: boolean;
}) {
    const min = (CARD_SIZES.find((s) => s.k === size) ?? CARD_SIZES[1]).min;
    const [head, ...rest] = cols;
    if (!head) return null;
    return (
        <div style={{
            display: 'grid', gap: 14, padding: flush ? 0 : 18, minWidth: 0,
            gridTemplateColumns: `repeat(auto-fill,minmax(min(100%,${min}px),1fr))`,
        }}>
            {rows.map((row, i) => (
                <article key={rowKey(row, i)} style={{
                    border: `1px solid ${v('border')}`, borderRadius: 14, background: v('surface'),
                    padding: 14, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
                }}>
                    <header style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}>{head.cell(row)}</header>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(104px,1fr))', gap: '10px 14px' }}>
                        {rest.map((col) => (
                            <div key={col.k} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                                <span title={col.title} style={{ fontSize: 10.5, color: v('muted'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {col.l}
                                </span>
                                <span className="rv-mono" style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {col.cell(row)}
                                </span>
                            </div>
                        ))}
                    </div>
                </article>
            ))}
        </div>
    );
}
